/**
 * A work week in the shortest form that still reads: "Mon–Thu", "Mon, Wed, Fri", "Tue–Sat".
 *
 * Distinct from `workWeekAsText`, which spells every day out in full. This one is for listing many
 * people's weeks side by side, where the full form would be a wall of text.
 */

const DAY_COUNT = 7

/** A run collapses to a range only from three days up: "Mon, Tue" is no longer than "Mon–Tue". */
const MIN_RANGE_LENGTH = 3

export interface ICompactWorkWeekParams {
  days: number[]
  /** 0 = Sunday, 1 = Monday. Decides where the week starts, and so where a run can begin. */
  firstDayOfWeek: number
  /** Seven short names indexed by day number, Sunday first, already translated. */
  shortDayNames: string[]
}

export function compactWorkWeek({ days, firstDayOfWeek, shortDayNames }: ICompactWorkWeekParams): string {
  const weekOrder = Array.from({ length: DAY_COUNT }, (_, offset) => (firstDayOfWeek + offset) % DAY_COUNT)
  const worked = new Set(days)
  const selected = weekOrder.filter((day) => worked.has(day))
  if (selected.length === 0) {
    return ''
  }

  // Runs are consecutive in the week's own order, not in day number: with the week starting on
  // Monday, Sunday is the last position, so Sat+Sun is a run and Sun+Mon is not.
  const runs: number[][] = []
  let previousPosition = -2
  for (const day of selected) {
    const position = weekOrder.indexOf(day)
    if (position === previousPosition + 1) {
      runs[runs.length - 1].push(day)
    } else {
      runs.push([day])
    }
    previousPosition = position
  }

  return runs
    .flatMap((run) => (run.length >= MIN_RANGE_LENGTH ? [`${shortDayNames[run[0]]}–${shortDayNames[run[run.length - 1]]}`] : run.map((day) => shortDayNames[day])))
    .join(', ')
}
